/**
 * Shared Instagram-style reels carousel (homepage + product page).
 * window.ManaReels.init(root)
 */
(() => {
  function init(root) {
    const track = root.querySelector("[data-reels-track]");
    if (!track || track.dataset.ready === "true") return;
    const originals = [...track.children];
    if (!originals.length) return;
    track.dataset.ready = "true";

    let unmutedKey = null;

    function syncSound() {
      track.querySelectorAll(".reel").forEach((card) => {
        const on = unmutedKey != null && card.dataset.reelKey === unmutedKey;
        const video = card.querySelector(".reel__media video");
        const btn = card.querySelector("[data-reel-sound]");
        if (video) video.muted = !on;
        if (btn) {
          btn.classList.toggle("is-on", on);
          btn.setAttribute("aria-pressed", on ? "true" : "false");
          btn.setAttribute("aria-label", on ? "Mute reel" : "Unmute reel");
        }
      });
    }

    function unloadVideo(v) {
      v.pause();
      if (v.getAttribute("src") || v.src) {
        v.removeAttribute("src");
        v.load();
      }
    }

    function armVideo(v) {
      const src = v.dataset.src;
      if (!src) return;
      const card = v.closest(".reel");
      const shouldUnmute = unmutedKey != null && card && card.dataset.reelKey === unmutedKey;
      v.loop = true;
      v.playsInline = true;
      v.muted = !shouldUnmute;
      v.preload = "auto";
      if (v.getAttribute("src") !== src) v.src = src;
      if (v.ended) v.currentTime = 0;
      if (!v.paused && v.readyState > 2) return;
      v.play().catch(() => {
        v.addEventListener("canplay", () => v.play().catch(() => {}), { once: true });
      });
    }

    function playVisible() {
      const viewport = root.querySelector(".reels__viewport");
      if (!viewport) return;
      const box = viewport.getBoundingClientRect();
      const visible = [];
      track.querySelectorAll(".reel__media video").forEach((v) => {
        const r = v.getBoundingClientRect();
        const on = r.right > box.left + 8 && r.left < box.right - 8;
        if (on) visible.push(v);
        else unloadVideo(v);
      });
      visible.forEach((v, i) => {
        if (v.getAttribute("src")) {
          armVideo(v);
          return;
        }
        window.setTimeout(() => {
          const r = v.getBoundingClientRect();
          const stillOn = r.right > box.left + 8 && r.left < box.right - 8;
          if (stillOn) armVideo(v);
        }, i * 120);
      });
      syncSound();
    }

    function bindVideoLoop() {
      track.querySelectorAll(".reel__media video").forEach((v) => {
        v.loop = true;
        v.playsInline = true;
        if (v.dataset.loopBound === "true") return;
        v.dataset.loopBound = "true";
        v.addEventListener("ended", () => {
          v.currentTime = 0;
          v.play().catch(() => {});
        });
        v.addEventListener("waiting", () => {
          window.setTimeout(() => {
            if (v.getAttribute("src") && v.paused === false) return;
            if (v.getAttribute("src")) v.play().catch(() => {});
          }, 400);
        });
        v.addEventListener("stalled", () => {
          if (!v.getAttribute("src")) return;
          const t = v.currentTime;
          v.load();
          v.currentTime = t;
          v.play().catch(() => {});
        });
      });
    }

    function onSoundClick(e) {
      const soundBtn = e.target.closest("[data-reel-sound]");
      if (!soundBtn) return false;
      e.preventDefault();
      e.stopPropagation();
      const card = soundBtn.closest(".reel");
      const key = card && card.dataset.reelKey;
      if (!key) return true;
      unmutedKey = unmutedKey === key ? null : key;
      const video = card.querySelector(".reel__media video");
      if (video && unmutedKey) {
        video.loop = true;
        video.play().catch(() => {});
      }
      syncSound();
      return true;
    }

    // Single reel: no infinite carousel
    if (originals.length < 2) {
      bindVideoLoop();
      playVisible();
      window.addEventListener("resize", playVisible);
      track.addEventListener("click", (e) => { onSoundClick(e); }, true);
      if (typeof IntersectionObserver !== "undefined") {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((en) => en.isIntersecting)) playVisible();
        }, { threshold: 0.15 });
        io.observe(root);
      }
      return;
    }

    originals.forEach((el) => track.appendChild(el.cloneNode(true)));

    let index = 0;
    let step = 0;
    let timer;
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startIndex = 0;
    const count = originals.length;

    function measure() {
      const card = track.querySelector(".reel");
      if (!card) return;
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 12;
      step = card.getBoundingClientRect().width + gap;
    }

    function paint(instant) {
      track.style.transition = instant ? "none" : "transform .45s ease";
      track.style.transform = `translateX(${-index * step}px)`;
      window.setTimeout(playVisible, instant ? 30 : 480);
    }

    function go(dir) {
      if (dir < 0 && index === 0) {
        index = count;
        paint(true);
        requestAnimationFrame(() => {
          index = count - 1;
          paint(false);
        });
        return;
      }
      index += dir;
      paint(false);
      if (index >= count) {
        window.setTimeout(() => {
          index = 0;
          paint(true);
        }, 460);
      }
    }

    function play() {
      clearInterval(timer);
      timer = setInterval(() => go(1), Number(root.dataset.interval) || 10000);
    }

    bindVideoLoop();
    measure();
    paint(true);
    play();

    root.querySelector("[data-reels-prev]")?.addEventListener("click", () => { go(-1); play(); });
    root.querySelector("[data-reels-next]")?.addEventListener("click", () => { go(1); play(); });
    root.addEventListener("mouseenter", () => clearInterval(timer));
    root.addEventListener("mouseleave", () => { if (!dragging) play(); });
    window.addEventListener("resize", () => { index = 0; measure(); paint(true); });

    track.addEventListener("pointerdown", (e) => {
      moved = false;
      if (e.target.closest("button, a")) return;
      dragging = true;
      startX = e.clientX;
      startIndex = index;
      track.setPointerCapture(e.pointerId);
      track.classList.add("is-dragging");
      clearInterval(timer);
    });
    track.addEventListener("pointermove", (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 8) moved = true;
      track.style.transition = "none";
      track.style.transform = `translateX(${-(startIndex * step) + dx}px)`;
    });
    track.addEventListener("pointerup", (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      dragging = false;
      track.classList.remove("is-dragging");
      if (dx > 50) go(-1);
      else if (dx < -50) go(1);
      else paint(false);
      play();
    });
    track.addEventListener("pointercancel", () => {
      dragging = false;
      track.classList.remove("is-dragging");
      paint(false);
      play();
    });
    track.addEventListener("click", (e) => {
      if (onSoundClick(e)) {
        moved = false;
        return;
      }
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);
  }

  window.ManaReels = { init };
})();
